import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { appStore } from '../../app/AppStore';
import { IPlaceScope } from '../../app/IPlaceScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { Location } from '../../common/react/Location';
import { FlowPlaceScope } from '../controllers/FlowPlaceCtrl';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { FlowContentType } from '../models/ContentTypes';
import { FlowBasicInfo, FlowDetailInfo, FlowListItemInfo } from '../models/FlowInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { FlowAllowedActions } from '../ServerApi/types/FlowAllowedActions';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from './ActionHelpers';
import { IActionButton } from './ActionPanelService';
import { ChangeFlowOwnerAction } from './ChangeFlowOwnerAction';
import { ChangeFlowsTagsAction } from './ChangeFlowsTagsAction';
import { DeleteFlowAction } from './DeleteFlowAction';
import { DownloadActions } from './DownloadActions';
import { EditPermissionsAction } from './EditPermissionsAction';
import { MoveToProjectAction } from './MoveToProjectAction';
import { RenameAction } from './RenameAction';
import { RevisionHistoryAction } from './RevisionHistoryAction';
import { RunFlowAction } from './RunFlowAction';

export class FlowActions {

  public static $inject = [
    'ChangeFlowOwnerAction',
    'ChangeFlowsTagsAction',
    'DeleteFlowAction',
    'DownloadActions',
    'EditPermissionsAction',
    'MoveToProjectAction',
    'RenameAction',
    'RevisionHistoryAction',
    'RunFlowAction'
  ];

  private flowsActionDefinition: IActionButton<FlowListItemInfo, FlowDetailInfo>[]; // actions taken on flows
  private flowsActionDefinitionWithRunAndDownload: IActionButton<FlowListItemInfo, FlowDetailInfo>[];

  constructor(
    private ChangeFlowOwnerAction: ChangeFlowOwnerAction,
    private ChangeFlowsTagsAction: ChangeFlowsTagsAction,
    private DeleteFlowAction: DeleteFlowAction,
    private DownloadActions: DownloadActions,
    private EditPermissionsAction: EditPermissionsAction,
    private MoveToProjectAction: MoveToProjectAction,
    private RenameAction: RenameAction,
    private RevisionHistoryAction: RevisionHistoryAction,
    private RunFlowAction: RunFlowAction
  ) {

    this.flowsActionDefinition = [
      this.changeFlowsTagsAction(),
      this.changeFlowOwnerAction(),
      this.permissionsAction(),
      this.moveFlowAction(),
      this.renameFlowAction(),
      this.revisionHistoryAction(),
      this.deleteFlowAction(),
    ];

    this.flowsActionDefinitionWithRunAndDownload = [
      this.runFlowAction(),
      this.downloadFlowAction()
    ].concat(this.flowsActionDefinition);
  }

  // public api

  public getFlowsActionButtons(): IActionButton<FlowListItemInfo, FlowDetailInfo>[] {
    return this.flowsActionDefinition;
  }

  public getFlowsActionsWithRunAndDownload(): IActionButton<FlowListItemInfo, FlowDetailInfo>[] {
    return this.flowsActionDefinitionWithRunAndDownload;
  }

  public deleteFlow(flow: FlowListItemInfo, dialogScope: ng.IScope): ng.IPromise<any> {
    const flows = Set.singletonForIdItem(flow);
    return this.DeleteFlowAction.showDeleteFlowDialog(flows, dialogScope);
  }

  public runNow(flow: FlowListItemInfo, context: IActionContext): ng.IPromise<any> {
    const flows = Set.singletonForIdItem(flow);
    return this.RunFlowAction.showRunFlowDialog(flows, context);
  }

  public download(flow: FlowBasicInfo): ng.IPromise<any> {
    return this.DownloadActions.downloadFlow(flow);
  }

  // action button factories

  private runFlowAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'runNow',
      translate: 'runNow_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return !!allowedActions && !allowedActions.runFlow;
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext, detailedItem?: FlowDetailInfo) => {
        const scope = <RefreshContentScope>context.scope;
        this.RunFlowAction.showRunFlowDialog(items, context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private changeFlowsTagsAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'tags',
      translate: 'tag_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return !!allowedActions && !allowedActions.setTags;
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext, detailedItem?: FlowDetailInfo) => {
        const scope = <RefreshContentScope>context.scope;
        this.ChangeFlowsTagsAction.execute(items, context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private changeFlowOwnerAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'changeOwner',
      translate: 'changeOwner_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return !!allowedActions && !allowedActions.setOwner;
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.ChangeFlowOwnerAction.confirmChangeOwner(items, context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private downloadFlowAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'download',
      translate: 'download',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return (!!allowedActions && !allowedActions.download) || !FlowActions.oneSelected(items);
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext, detailedItem?: FlowDetailInfo) => {
        const flow = detailedItem || items.first();
        const scope = <FlowPlaceScope>context.scope;
        this.DownloadActions.downloadFlow(flow)
          .catch(() => scope.refreshContent()); // May have failed this action was taken on stale information
      }
    };
  }

  private permissionsAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'permissions',
      translate: 'permissions_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return !!allowedActions && !allowedActions.setPermissions;
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext) => {
        this.EditPermissionsAction.showEditPermissionsDialog(items, FlowContentType.name, context);
      }
    };
  }

  private moveFlowAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'move',
      translate: 'move_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return !!allowedActions && !allowedActions.setProject;
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope>context.scope;
        this.MoveToProjectAction.moveFlows(items, context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private renameFlowAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'rename',
      translate: 'rename_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return (!!allowedActions && !allowedActions.setName) || !FlowActions.oneSelected(items);
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext, detailedItem?: FlowDetailInfo) => {
        const flow = detailedItem || items.first();
        const scope = <RefreshContentScope>context.scope;
        this.RenameAction.renameFlow(flow, context)
          .then(() => scope.refreshContent());
      }
    };
  }

  private revisionHistoryAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'revisionHistory',
      translate: 'revisionHistory_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return (!!allowedActions && !allowedActions.versionManagement) || !FlowActions.oneSelected(items);
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext) => {
        const flow = items.first();
        this.RevisionHistoryAction.showFlowRevisionHistory(flow);
      }
    };
  }

  private deleteFlowAction(): IActionButton<FlowListItemInfo, FlowDetailInfo> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: FlowActions.anySelected,
      isDisabled: (items: Set<FlowListItemInfo>, detailedItem?: FlowDetailInfo, allowedActions?: FlowAllowedActions) => {
        return !!allowedActions && !allowedActions.del;
      },
      execute: (items: Set<FlowListItemInfo>, context: IActionContext) => {
        const scope = <RefreshContentScope & IPlaceScope>context.scope;
        const projectId = items.first().project.id;
        this.DeleteFlowAction.showDeleteFlowDialog(items, scope).then((result: IResult) => {
          if (scope.place.name === 'flow') {
            const siteUrls = siteUrlsSelector(appStore.getState());
            Location.instance.setUrl(siteUrls.projectUrl(projectId));
          } else {
            // need Flow error codes, this is now using the GroupNotFound error
            ActionHelpers.deselectAllExceptErrors(items, result, CommonErrorCode.WorkbookNotFound);
            scope.refreshContent();
          }
        });
      }
    };
  }

  // helpers

  public static anySelected(items: Set<FlowListItemInfo>): boolean {
    return items.size() > 0;
  }

  public static oneSelected(items: Set<FlowListItemInfo>): boolean {
    return items.size() === 1;
  }

}

Module.instance().service('FlowActions', FlowActions);
